import { RteColorPicker as NativeColorPicker } from '@extensions/components/Form/RteColorPicker';
import { IconFont } from '@bay-email/editor';
import React, { useMemo } from 'react';
import { ToolItem } from '../../ToolItem';
import { findDynamicColorKey } from '../dynamicColorVar';
import { t } from '@bay-email/core';

interface BrandColor {
	label: string;
	color: string;
	value: string;
}

export function IconFontColor({
	selectionRange,
	execCommand,
	getPopoverMountNode,
	onBeforeInteract,
	onCommit,
	brandColors,
}: {
	selectionRange: Range | null;
	execCommand: (cmd: string, val?: any, opts?: { dynamicTag?: string }) => void;
	getPopoverMountNode: () => HTMLElement;
	onBeforeInteract?: () => void;
	onCommit?: () => void;
	brandColors?: BrandColor[];
}) {
	const color = useMemo(() => {
		if (!selectionRange) return undefined;
		const container = selectionRange.commonAncestorContainer;
		const element =
			container instanceof HTMLElement
				? container
				: container.parentNode instanceof HTMLElement
					? container.parentNode
					: null;
		if (!element) return undefined;

		// Prefer the dynamic brand-colour merge-tag KEY recorded on the wrapper (stored
		// brace-less, e.g. "user.brandPrimaryColor") so the picker re-opens in Dynamic
		// mode and remembers the brand selection. Re-wrap it as "{{...}}" to match the
		// brand swatch values. Fall back to the computed hex for static colours.
		const dynamicKey = findDynamicColorKey(selectionRange, element, 'data-bay-color-var');
		if (dynamicKey) return `{{${dynamicKey}}}`;

		return getComputedStyle(element).color;
	}, [selectionRange]);

	// The toolbar icon's underline needs a real colour; when `color` is a brand merge
	// tag, show the brand's resolved hex instead of the (uncolourable) tag string.
	const displayColor = useMemo(
		() => brandColors?.find(b => b.value === color)?.color ?? color,
		[brandColors, color],
	);

	return (
		<NativeColorPicker
			value={color}
			brandColors={brandColors}
			supportsDynamic
			onChange={nextColor => {
				// A brand swatch emits its merge tag (e.g. {{user.brandPrimaryColor}}).
				// Apply the resolved hex for the visible colour, and carry the tag so the
				// serialized output stays dynamic.
				const brand = brandColors?.find(b => b.value === nextColor);
				if (brand) {
					execCommand('foreColor', brand.color, { dynamicTag: brand.value });
				} else {
					execCommand('foreColor', nextColor);
				}
				// Re-snapshot after each execCommand: the DOM mutated (new span wrapping),
				// so lastValidRangeRef must be updated to the post-mutation selection.
				onBeforeInteract?.();
			}}
			onDynamicCommit={(brandValue, brandHex) => {
				// Dynamic mode: apply the brand's resolved hex as the visible colour and
				// carry the merge tag so the dynamic substitution happens at send time.
				execCommand('foreColor', brandHex, { dynamicTag: brandValue });
				onBeforeInteract?.();
			}}
			onCommit={onCommit}
			getPopupContainer={getPopoverMountNode}
			highlightRange={selectionRange}
			showInput={false}
			onBeforeOpen={onBeforeInteract}
		>
			<ToolItem
				icon={
					<div style={{ position: 'relative' }}>
						<IconFont
							size={12}
							iconName='icon-font-color'
							style={{ position: 'relative', top: '-1px' }}
						/>
						<div
							style={{
								borderBottom: `2px solid ${displayColor}`,
								position: 'absolute',
								width: '130%',
								left: '-15%',
								top: 16,
							}}
						/>
					</div>
				}
				title={t('Text Color')}
			/>
		</NativeColorPicker>
	);
}
